import React, { useEffect, useState, useCallback, useRef } from 'react';

/* ------------------------------------------------------------------ */
/*  Lightweight toast system – no external dependencies.               */
/*  Usage:                                                             */
/*    import { toast } from '../components/Toast';                     */
/*    toast.success('Copied!');                                        */
/*    toast.error('Something went wrong');                             */
/*                                                                     */
/*  Mount <Toaster /> once at the app root (e.g. index.tsx).           */
/* ------------------------------------------------------------------ */

type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

// ---- global state shared between toast() calls and <Toaster /> ----
let listeners: Array<(items: ToastItem[]) => void> = [];
let toasts: ToastItem[] = [];
let nextId = 0;

function emit() {
  listeners.forEach((l) => l([...toasts]));
}

function addToast(type: ToastType, message: string, duration = 3000) {
  const id = nextId++;
  toasts = [...toasts, { id, type, message }];
  emit();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, duration);
}

/** Imperative toast API */
export const toast = {
  success: (msg: string, duration?: number) => addToast('success', msg, duration),
  error: (msg: string, duration?: number) => addToast('error', msg, duration),
  info: (msg: string, duration?: number) => addToast('info', msg, duration),
};

// ---- Icons ----
const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
      clipRule="evenodd"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
      clipRule="evenodd"
    />
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
      clipRule="evenodd"
    />
  </svg>
);

const ICON_MAP: Record<ToastType, React.FC> = {
  success: SuccessIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

const COLOR_MAP: Record<ToastType, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-white dark:bg-slate-900',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-gray-800 dark:text-gray-100',
    icon: 'text-green-500 dark:text-green-400',
  },
  error: {
    bg: 'bg-white dark:bg-slate-900',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-gray-800 dark:text-gray-100',
    icon: 'text-red-500 dark:text-red-400',
  },
  info: {
    bg: 'bg-white dark:bg-slate-900',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-gray-800 dark:text-gray-100',
    icon: 'text-blue-500 dark:text-blue-400',
  },
};

// ---- Single toast element ----
const ToastElement: React.FC<{ item: ToastItem; onDismiss: (id: number) => void }> = ({ item, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const colors = COLOR_MAP[item.type];
  const Icon = ICON_MAP[item.type];

  useEffect(() => {
    // trigger enter animation on next frame
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={() => onDismiss(item.id)}
      className={`
        flex items-center gap-2.5 px-4 py-3 rounded-lg border shadow-lg cursor-pointer
        transition-all duration-300 ease-out
        ${colors.bg} ${colors.border} ${colors.text}
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}
      `}
      style={{ fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 400, pointerEvents: 'auto' }}
    >
      <span className={`flex-shrink-0 ${colors.icon}`}>
        <Icon />
      </span>
      <span>{item.message}</span>
    </div>
  );
};

// ---- Toaster container (mount once at app root) ----
export const Toaster: React.FC = () => {
  const [items, setItems] = useState<ToastItem[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    const handler = (next: ToastItem[]) => setItems(next);
    listeners.push(handler);
    return () => {
      listeners = listeners.filter((l) => l !== handler);
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2"
      style={{ pointerEvents: 'none' }}
    >
      {items.map((item) => (
        <ToastElement key={item.id} item={item} onDismiss={dismiss} />
      ))}
    </div>
  );
};
